import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, Trash2, CheckCircle2, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";
import { fmtMoney } from "../config";
import Seo from "../components/Seo";
import ActivityFeed from "../components/ActivityFeed";
import ChatTranscripts from "../components/ChatTranscripts";
import CustomerDetail from "../components/CustomerDetail";
import LeadDetailDrawer from "../components/LeadDetailDrawer";
import ManagerDashboard from "../components/ManagerDashboard";
import NewLeadToast from "../components/NewLeadToast";
import QuickAddLeadModal from "../components/QuickAddLeadModal";
import TodayInbox from "../components/TodayInbox";
import DripSettingsPanel from "../components/DripSettingsPanel";
import KanbanBoard from "../components/KanbanBoard";
import KpiStrip from "../components/KpiStrip";
import WinRateDashboard from "../components/WinRateDashboard";
import AppointmentManager from "../components/AppointmentManager";
import SettingsTab from "../components/SettingsTab";

const BASE_TABS = ["Today", "Pipeline", "Appointments", "Win Rate", "Overview", "Metrics", "My Activity", "Conversations", "Leads", "Deals", "Customers", "Auto-Drip", "Search by Code"];
const SETTINGS_ROLES = new Set(["owner", "super_admin", "dealer", "admin", "sales_manager"]);

export default function DealerPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("Today");
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [overview, setOverview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [leadFilter, setLeadFilter] = useState({ source: "", contacted: "", q: "", tag: "" });
  const [dealSearch, setDealSearch] = useState("");
  const [code, setCode] = useState("");
  const [codeResult, setCodeResult] = useState(null);
  const [codeErr, setCodeErr] = useState("");
  const [openLeadId, setOpenLeadId] = useState(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [availableTags, setAvailableTags] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);

  // Auth gate
  useEffect(() => {
    if (user === false) nav("/dealer-login", { replace: true });
    else if (user && !isDealerUser(user)) nav("/my-account", { replace: true });
  }, [user, nav]);

  const loadOverview = useCallback(async () => {
    const { data } = await api.get("/dealer/overview");
    setOverview(data);
  }, []);

  const loadLeads = useCallback(async () => {
    const params = {};
    if (leadFilter.source) params.source = leadFilter.source;
    if (leadFilter.contacted !== "") params.contacted = leadFilter.contacted === "true";
    if (leadFilter.q) params.q = leadFilter.q;
    const { data } = await api.get("/dealer/leads", { params });
    let items = data.items || [];
    if (leadFilter.tag) items = items.filter((l) => (l.tags || []).includes(leadFilter.tag));
    setLeads(items);
    setSelectedLeads(new Set());
  }, [leadFilter]);

  // Load tags + saved searches once
  useEffect(() => {
    if (!user || !isDealerUser(user)) return;
    api.get("/dealer/tags").then((r) => setAvailableTags(r.data.items || [])).catch(() => {});
    api.get("/dealer/saved-searches").then((r) => setSavedSearches(r.data.items || [])).catch(() => {});
  }, [user]);

  const applySavedSearch = (s) => {
    setLeadFilter({
      source: "",
      contacted: s.query.contacted ?? "",
      q: s.query.q || "",
      tag: s.query.tag || "",
    });
    setTab("Leads");
  };

  const toggleSelect = (id) => {
    setSelectedLeads((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selectedLeads.size === leads.length) setSelectedLeads(new Set());
    else setSelectedLeads(new Set(leads.map((l) => l.id)));
  };

  const bulkAction = async (action, value) => {
    if (selectedLeads.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${selectedLeads.size} leads? This cannot be undone.`)) return;
    await api.post("/dealer/leads/bulk", {
      lead_ids: Array.from(selectedLeads), action, value: value || null,
    });
    setSelectedLeads(new Set());
    await loadLeads();
  };

  const loadDeals = useCallback(async () => {
    const params = dealSearch ? { q: dealSearch } : {};
    const { data } = await api.get("/dealer/deals", { params });
    setDeals(data.items || []);
  }, [dealSearch]);

  const loadCustomers = useCallback(async () => {
    const { data } = await api.get("/dealer/customers");
    setCustomers(data.items || []);
  }, []);

  useEffect(() => {
    if (!user || !isDealerUser(user)) return;
    if (tab === "Overview") loadOverview();
    else if (tab === "Leads") loadLeads();
    else if (tab === "Deals") loadDeals();
    else if (tab === "Customers") loadCustomers();
  }, [tab, user, loadOverview, loadLeads, loadDeals, loadCustomers]);

  const lookupCode = async (e) => {
    e?.preventDefault?.();
    setCodeErr(""); setCodeResult(null);
    try {
      const { data } = await api.get(`/dealer/deal/${encodeURIComponent(code.trim().toUpperCase())}`);
      setCodeResult(data.deal);
    } catch {
      setCodeErr("No deal found with that code.");
    }
  };

  const markContacted = async (id) => {
    await api.post(`/leads/${id}/mark-contacted`);
    setLeads((arr) => arr.map((l) => l.id === id ? { ...l, contacted: true } : l));
  };

  const exportLeadsCsv = async () => {
    const params = {};
    if (leadFilter.source) params.source = leadFilter.source;
    if (leadFilter.contacted !== "") params.contacted = leadFilter.contacted === "true";
    if (leadFilter.q) params.q = leadFilter.q;
    try {
      const resp = await api.get("/dealer/leads.csv", {
        params,
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-");
      a.href = url; a.download = `ssc-leads-${stamp}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.message === "Export cancelled.") return;
      const detail = e?.response?.data?.detail || e?.message || "Export failed";
      alert(`Export blocked: ${detail}`);
    }
  };

  if (!user || !isDealerUser(user)) {
    return <div className="p-12 text-center text-[var(--ssc-ink-2)]">Verifying access…</div>;
  }

  return (
    <main id="main-content" className="bg-[var(--ssc-navy)] min-h-screen pb-16">
      <Seo title="Dealer Portal | Scott Sells Chevys" description="" />

      {/* Header */}
      <div className="border-b border-white/10 px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-extrabold tracking-[0.25em] uppercase text-[var(--ssc-blue)]">Dealer Portal</div>
          <div className="text-white font-black text-base sm:text-2xl truncate">Scott Sells Chevys</div>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <Link to="/admin/today" className="ssc-btn ssc-btn-primary text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4" data-testid="dealer-open-today">Today</Link>
          <Link to="/admin/pipeline" className="ssc-btn ssc-btn-secondary text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4" data-testid="dealer-open-pipeline">Pipeline</Link>
          <Link to="/admin/crm" className="ssc-btn ssc-btn-secondary text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4" data-testid="dealer-open-crm">CRM / Deal Jacket</Link>
          <Link to="/admin/conversations" className="hidden md:inline ssc-btn ssc-btn-secondary text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4" data-testid="dealer-open-convos">Conversations</Link>
          <Link to="/admin/reports" className="hidden lg:inline ssc-btn ssc-btn-secondary text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4" data-testid="dealer-open-reports">Reports</Link>
          <Link to="/" className="hidden sm:inline text-xs text-white/60 hover:text-white whitespace-nowrap">← View site</Link>
          <button onClick={async () => { await logout(); nav("/"); }} className="ssc-btn ssc-btn-secondary text-xs sm:text-sm whitespace-nowrap px-3 sm:px-4" data-testid="dealer-logout">Sign out</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-4 flex gap-2 overflow-x-auto px-4 sm:px-8 pb-1 scrollbar-thin" data-testid="dealer-tabs">
        {(SETTINGS_ROLES.has(user?.role) ? [...BASE_TABS, "Settings"] : BASE_TABS).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`dealer-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${tab === t ? "bg-[var(--ssc-blue)] text-white" : "bg-white/5 text-white/60 hover:text-white"}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="px-4 sm:px-8 mt-6">
        {tab === "Today" && (
          <div className="space-y-4">
            <KpiStrip />
            <TodayInbox onOpenLead={(id) => setOpenLeadId(id)} />
          </div>
        )}

        {tab === "Pipeline" && <KanbanBoard onOpenLead={(id) => setOpenLeadId(id)} />}

        {tab === "Appointments" && <AppointmentManager onOpenLead={(id) => setOpenLeadId(id)} />}

        {tab === "Win Rate" && <WinRateDashboard />}

        {tab === "Auto-Drip" && <DripSettingsPanel />}

        {tab === "Settings" && <SettingsTab />}

        {tab === "Overview" && overview && (
          <div data-testid="dealer-overview" className="space-y-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <Stat label="Total Leads" value={overview.leads_total} accent />
              <Stat label="Uncontacted" value={overview.leads_uncontacted} highlight />
              <Stat label="Last 24h" value={overview.leads_24h} />
              <Stat label="Last 7d" value={overview.leads_7d} />
              <Stat label="Total Deals" value={overview.deals_total} accent />
              <Stat label="Open Deals" value={overview.deals_open} />
              <Stat label="Customers" value={overview.customers_total} />
              <div className="bg-white/5 rounded-xl p-4 col-span-2 lg:col-span-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-silver)] mb-2">Lead Sources</div>
                <ul className="text-xs space-y-1">
                  {overview.sources.slice(0, 6).map((s) => (
                    <li key={s.source} className="flex justify-between text-white">
                      <span className="truncate">{s.source}</span>
                      <span className="font-bold text-[var(--ssc-blue)] ml-2">{s.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 🔥 HOT RIGHT NOW widget */}
            <div className="bg-gradient-to-br from-red-500/10 to-orange-500/5 border border-red-500/30 rounded-2xl p-5" data-testid="overview-hot">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                  <div className="text-[10px] font-extrabold uppercase tracking-widest text-red-400">🔥 Hot Right Now</div>
                  <div className="text-white font-black text-lg">Customers with 3+ touchpoints in the last 24 hours</div>
                </div>
                <button onClick={loadOverview} className="text-xs text-white/70 hover:text-white">Refresh</button>
              </div>
              {(!overview.hot_right_now || overview.hot_right_now.length === 0) ? (
                <div className="text-white/60 text-sm py-4" data-testid="overview-hot-empty">
                  Nobody's piling on yet — check back later. The activity feed shows everything as it happens.
                </div>
              ) : (
                <ul className="grid sm:grid-cols-2 gap-3" data-testid="overview-hot-list">
                  {overview.hot_right_now.map((h, i) => (
                    <li key={i} className="bg-white rounded-xl p-3 flex items-start gap-3" data-testid={`overview-hot-row-${i}`}>
                      <div className="shrink-0 w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center font-black text-sm">
                        {h.events}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-[var(--ssc-ink)]">{h.name}</span>
                          {h.deal_code && <span className="font-mono text-xs font-bold text-[var(--ssc-navy)]">{h.deal_code}</span>}
                          {h.amount ? <span className="text-xs font-bold text-[var(--ssc-blue)]">{fmtMoney(h.amount)}</span> : null}
                        </div>
                        <div className="text-xs text-[var(--ssc-ink-2)]">
                          {h.email && <a className="text-[var(--ssc-blue)]" href={`mailto:${h.email}`}>{h.email}</a>}
                          {h.phone && <span> · <a className="text-[var(--ssc-ink)]" href={`tel:${h.phone}`}>{h.phone}</a></span>}
                        </div>
                        <div className="text-xs text-[var(--ssc-ink)] mt-1 truncate">
                          {(h.types || []).slice(0, 4).join(" · ")}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === "My Activity" && <ActivityFeed />}

        {tab === "Metrics" && <ManagerDashboard />}

        {tab === "Conversations" && <ChatTranscripts />}

        {tab === "Leads" && (
          <div data-testid="dealer-leads">
            {/* Saved searches chips */}
            {savedSearches.length > 0 && (
              <div className="flex gap-1.5 flex-wrap mb-3" data-testid="saved-searches">
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-white/60 self-center mr-1">Smart filters:</span>
                {savedSearches.map((s) => (
                  <button key={s.id} onClick={() => applySavedSearch(s)}
                          className="text-xs font-bold px-2.5 py-1 rounded-full bg-white/5 text-white/80 hover:bg-white/10 border border-white/10"
                          data-testid={`saved-search-${s.id}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mb-4">
              <input className="ssc-dealer-input" placeholder="Search name, email, code…" value={leadFilter.q}
                     onChange={(e) => setLeadFilter({ ...leadFilter, q: e.target.value })}
                     onKeyDown={(e) => e.key === "Enter" && loadLeads()} data-testid="leads-search" />
              <select className="ssc-dealer-input" value={leadFilter.source} onChange={(e) => setLeadFilter({ ...leadFilter, source: e.target.value })} data-testid="leads-source">
                <option value="">All sources</option>
                <option value="Website Chatbot">Website Chatbot</option>
                <option value="trade-pdf-email">Email My Estimate</option>
                <option value="Contact Page">Contact Page</option>
                <option value="BYOD">Build My Own Deal</option>
                <option value="Trade Submit">Trade Submit</option>
                <option value="Walk-In">Walk-In</option>
                <option value="Phone Call">Phone Call</option>
              </select>
              <select className="ssc-dealer-input" value={leadFilter.contacted} onChange={(e) => setLeadFilter({ ...leadFilter, contacted: e.target.value })} data-testid="leads-status">
                <option value="">All status</option>
                <option value="false">Uncontacted</option>
                <option value="true">Contacted</option>
              </select>
              {availableTags.length > 0 && (
                <select className="ssc-dealer-input" value={leadFilter.tag} onChange={(e) => setLeadFilter({ ...leadFilter, tag: e.target.value })} data-testid="leads-tag">
                  <option value="">All tags</option>
                  {availableTags.map((t) => <option key={t.tag} value={t.tag}>{t.tag} ({t.count})</option>)}
                </select>
              )}
              <button onClick={loadLeads} className="ssc-btn ssc-btn-primary text-xs px-4" data-testid="leads-apply">Apply</button>
              <button onClick={() => setQuickAddOpen(true)} className="ssc-btn ssc-btn-primary text-xs px-4 inline-flex items-center gap-1" data-testid="leads-quick-add">
                <UserPlus size={13} /> Add Lead
              </button>
              <button onClick={exportLeadsCsv} className="ssc-btn ssc-btn-secondary text-xs px-4" data-testid="leads-export-csv">
                Export CSV
              </button>
            </div>

            {/* Bulk actions bar */}
            {selectedLeads.size > 0 && (
              <div className="bg-[var(--ssc-blue)]/10 border border-[var(--ssc-blue)]/30 rounded-xl p-3 mb-3 flex items-center gap-2 flex-wrap" data-testid="bulk-bar">
                <span className="text-sm font-bold text-white">
                  {selectedLeads.size} selected
                </span>
                <button onClick={() => bulkAction("mark_contacted")} className="ssc-btn ssc-btn-secondary text-xs px-3 inline-flex items-center gap-1" data-testid="bulk-contacted">
                  <CheckCircle2 size={13} /> Mark contacted
                </button>
                <select onChange={(e) => { if (e.target.value) { bulkAction("set_status", e.target.value); e.target.value = ""; } }} className="ssc-dealer-input text-xs h-8 py-0" data-testid="bulk-status">
                  <option value="">Set status…</option>
                  <option value="working">Working</option>
                  <option value="active">Needs Follow-up</option>
                  <option value="lost">Lost</option>
                </select>
                <select onChange={(e) => { if (e.target.value) { bulkAction("add_tag", e.target.value); e.target.value = ""; } }} className="ssc-dealer-input text-xs h-8 py-0" data-testid="bulk-tag">
                  <option value="">Add tag…</option>
                  <option value="Hot">Hot</option>
                  <option value="Cash buyer">Cash buyer</option>
                  <option value="Repeat customer">Repeat customer</option>
                  <option value="VIP">VIP</option>
                </select>
                <button onClick={() => bulkAction("delete")} className="text-xs font-bold text-red-300 hover:text-red-100 px-2 inline-flex items-center gap-1" data-testid="bulk-delete">
                  <Trash2 size={13} /> Delete
                </button>
                <button onClick={() => setSelectedLeads(new Set())} className="text-xs text-white/60 hover:text-white px-1 ml-auto" data-testid="bulk-clear">
                  <X size={13} />
                </button>
              </div>
            )}

            <div className="overflow-x-auto bg-white rounded-2xl">
              <table className="min-w-[700px] w-full text-sm">
                <thead className="bg-[var(--ssc-paper)] text-[var(--ssc-ink-2)] text-[11px] font-bold uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-3 py-2.5 w-8">
                      <input type="checkbox" checked={leads.length > 0 && selectedLeads.size === leads.length} onChange={selectAll} data-testid="bulk-select-all" />
                    </th>
                    <th className="text-left px-3 py-2.5">Date</th>
                    <th className="text-left px-3 py-2.5">Name</th>
                    <th className="text-left px-3 py-2.5">Contact</th>
                    <th className="text-left px-3 py-2.5">Source</th>
                    <th className="text-left px-3 py-2.5">Detail</th>
                    <th className="text-left px-3 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--ssc-line)]" data-testid="leads-table">
                  {leads.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[var(--ssc-ink-2)]">No leads match.</td></tr>}
                  {leads.map((l) => (
                    <tr key={l.id} className="hover:bg-[var(--ssc-paper)] cursor-pointer"
                        onClick={() => setOpenLeadId(l.id)}
                        data-testid={`lead-row-open-${l.id}`}>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedLeads.has(l.id)} onChange={() => toggleSelect(l.id)} data-testid={`lead-select-${l.id}`} />
                      </td>
                      <td className="px-3 py-2 text-xs text-[var(--ssc-ink-2)]">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2 font-semibold text-[var(--ssc-ink)]">
                        <button
                          onClick={() => setOpenLeadId(l.id)}
                          className="text-[var(--ssc-blue)] hover:underline text-left font-extrabold"
                          data-testid={`lead-open-${l.id}`}
                        >
                          {l.name || "—"}
                        </button>
                        {(l.tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {l.tags.map((t) => (
                              <span key={t} className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)] border border-[var(--ssc-blue)]/30">{t}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {l.email && <a href={`mailto:${l.email}`} className="text-[var(--ssc-blue)] block">{l.email}</a>}
                        {l.phone && <a href={`tel:${l.phone}`} className="text-[var(--ssc-ink-2)] block">{l.phone}</a>}
                      </td>
                      <td className="px-3 py-2 text-xs"><span className="inline-block bg-[var(--ssc-navy)] text-white px-2 py-0.5 rounded">{l.lead_source || l.lead_type || "—"}</span></td>
                      <td className="px-3 py-2 text-xs">
                        {l.vehicle_title && <div>{l.vehicle_title}</div>}
                        {l.deal_code && <div className="font-mono font-bold text-[var(--ssc-blue)]">{l.deal_code}</div>}
                        {l.message && <div className="text-[var(--ssc-ink-2)] line-clamp-2 max-w-[260px]">{l.message}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill status={l.status || "new"} onClick={() => setOpenLeadId(l.id)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "Deals" && (
          <div data-testid="dealer-deals">
            <div className="flex flex-wrap gap-2 mb-4">
              <input className="ssc-dealer-input" placeholder="Search code, name, vehicle…" value={dealSearch}
                     onChange={(e) => setDealSearch(e.target.value)}
                     onKeyDown={(e) => e.key === "Enter" && loadDeals()} data-testid="deals-search" />
              <button onClick={loadDeals} className="ssc-btn ssc-btn-primary text-xs px-4" data-testid="deals-apply">Search</button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {deals.length === 0 && <div className="text-white/60 col-span-full text-center py-8">No deals match.</div>}
              {deals.map((d) => (
                <div key={d.id} className="bg-white rounded-2xl p-4">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-blue)]">{d.deal_type}</span>
                    <span className="font-mono font-extrabold text-xs bg-[var(--ssc-navy)] text-white px-2 py-1 rounded">{d.code}</span>
                  </div>
                  <div className="font-black text-[var(--ssc-ink)]">{d.title || "—"}</div>
                  <div className="text-xs text-[var(--ssc-ink-2)] mt-1">{d.user_name} · {d.user_email}</div>
                  {d.estimate?.dealer_trade_high && (
                    <div className="text-sm font-bold text-[var(--ssc-blue)] mt-2">
                      Trade: {fmtMoney(d.estimate.dealer_trade_low)} – {fmtMoney(d.estimate.dealer_trade_high)}
                    </div>
                  )}
                  <div className="text-xs text-[var(--ssc-ink-2)] mt-2">{new Date(d.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Customers" && (
          activeCustomer ? (
            <CustomerDetail lookupKey={activeCustomer} onBack={() => setActiveCustomer(null)} />
          ) : (
            <div data-testid="dealer-customers" className="bg-white rounded-2xl overflow-x-auto">
            <table className="min-w-[700px] w-full text-sm">
              <thead className="bg-[var(--ssc-paper)] text-[var(--ssc-ink-2)] text-[11px] font-bold uppercase tracking-widest">
                <tr>
                  <th className="text-left px-3 py-2.5">Joined</th>
                  <th className="text-left px-3 py-2.5">Name</th>
                  <th className="text-left px-3 py-2.5">Email</th>
                  <th className="text-left px-3 py-2.5">Phone</th>
                  <th className="text-left px-3 py-2.5">Address</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--ssc-line)]">
                {customers.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[var(--ssc-ink-2)]">No customers yet.</td></tr>}
                {customers.map((c) => (
                  <tr key={c.id}
                      onClick={() => setActiveCustomer(c.id)}
                      className="hover:bg-[var(--ssc-paper)] cursor-pointer transition-colors"
                      data-testid={`customer-row-${c.id}`}>
                    <td className="px-3 py-2 text-xs text-[var(--ssc-ink-2)]">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td className="px-3 py-2 font-semibold text-[var(--ssc-blue)] underline">{c.name}</td>
                    <td className="px-3 py-2 text-xs">{c.email}</td>
                    <td className="px-3 py-2 text-xs">{c.phone}</td>
                    <td className="px-3 py-2 text-xs text-[var(--ssc-ink-2)]">{c.address || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )
        )}

        {tab === "Search by Code" && (
          <div data-testid="dealer-code" className="max-w-2xl">
            <form onSubmit={lookupCode} className="flex gap-2 mb-4">
              <input className="ssc-dealer-input flex-1" placeholder="SSC-XXXXXX" value={code}
                     onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="code-input" />
              <button className="ssc-btn ssc-btn-primary" data-testid="code-search">Look Up</button>
            </form>
            {codeErr && <div className="text-red-300 text-sm">{codeErr}</div>}
            {codeResult && (
              <div className="bg-white rounded-2xl p-5" data-testid="code-result">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-blue)]">{codeResult.deal_type}</div>
                    <h2 className="text-xl font-black text-[var(--ssc-navy)]">{codeResult.title}</h2>
                  </div>
                  <span className="font-mono font-extrabold bg-[var(--ssc-navy)] text-white px-3 py-1.5 rounded-md">{codeResult.code}</span>
                </div>
                <div className="mt-3 text-sm text-[var(--ssc-ink)]">
                  <strong>{codeResult.user_name}</strong><br/>
                  <a href={`mailto:${codeResult.user_email}`} className="text-[var(--ssc-blue)]">{codeResult.user_email}</a>
                  {codeResult.user_phone && <span className="text-[var(--ssc-ink-2)]"> · <a href={`tel:${codeResult.user_phone}`}>{codeResult.user_phone}</a></span>}
                </div>
                {codeResult.estimate?.dealer_trade_high && (
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <MiniStat label="Trade Low" value={fmtMoney(codeResult.estimate.dealer_trade_low)} />
                    <MiniStat label="Trade High" value={fmtMoney(codeResult.estimate.dealer_trade_high)} accent />
                    <MiniStat label="Retail" value={`${fmtMoney(codeResult.estimate.retail_low)}–${fmtMoney(codeResult.estimate.retail_high)}`} />
                  </div>
                )}
                {codeResult.vehicle && Object.keys(codeResult.vehicle).length > 0 && (
                  <div className="mt-4 p-3 bg-[var(--ssc-paper)] rounded-xl text-sm">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-ink-2)]">Vehicle</div>
                    <div className="font-bold mt-1">{codeResult.vehicle.year} {codeResult.vehicle.make} {codeResult.vehicle.model} {codeResult.vehicle.trim}</div>
                    {codeResult.vehicle.mileage && <div className="text-[var(--ssc-ink-2)] text-xs">{Number(codeResult.vehicle.mileage).toLocaleString()} miles · {codeResult.vehicle.condition || "—"} condition · ZIP {codeResult.vehicle.zip_code || "—"}</div>}
                  </div>
                )}
                {codeResult.dealer_notes?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-ink-2)] mb-2">Follow-up Notes</div>
                    <ul className="space-y-2">
                      {codeResult.dealer_notes.map((n) => (
                        <li key={n.id} className="text-sm bg-[var(--ssc-paper)] rounded-lg p-2.5">
                          <div className="text-xs text-[var(--ssc-ink-2)]">{new Date(n.at).toLocaleString()} · {n.by}</div>
                          <div className="text-[var(--ssc-ink)]">{n.note}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <AddNote code={codeResult.code} onAdded={(note) => setCodeResult({ ...codeResult, dealer_notes: [...(codeResult.dealer_notes || []), note] })} />
              </div>
            )}
          </div>
        )}
      </div>
      <NewLeadToast />
      <QuickAddLeadModal
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        onCreated={() => { loadLeads(); }}
      />
      {openLeadId && (
        <LeadDetailDrawer
          leadId={openLeadId}
          onClose={() => setOpenLeadId(null)}
          onChanged={loadLeads}
        />
      )}
    </main>
  );
}

const STATUS_COLORS = {
  new:     { bg: "#F4F7FB", fg: "#6b7c93", label: "New" },
  working: { bg: "#E8F0FF", fg: "#006DFF", label: "Working" },
  active:  { bg: "#FFF7E6", fg: "#f59e0b", label: "Follow-up" },
  sold:    { bg: "#E6FFF4", fg: "#10a37f", label: "Sold" },
  lost:    { bg: "#FFE9E9", fg: "#ef4444", label: "Lost" },
};

function StatusPill({ status, onClick }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs font-extrabold px-2.5 py-1 rounded-full hover:opacity-80 transition"
      style={{ background: c.bg, color: c.fg }}
      data-testid={`lead-status-${status}`}
    >
      {c.label}
    </button>
  );
}

const Stat = ({ label, value, accent, highlight }) => (
  <div className={`rounded-xl p-4 ${highlight ? "bg-[var(--ssc-blue)]" : accent ? "bg-white/10" : "bg-white/5"}`}>
    <div className={`text-[10px] font-bold uppercase tracking-widest ${highlight ? "text-white/80" : "text-[var(--ssc-silver)]"}`}>{label}</div>
    <div className={`text-3xl font-black mt-1 ${highlight ? "text-white" : "text-white"}`}>{value}</div>
  </div>
);

const MiniStat = ({ label, value, accent }) => (
  <div className={`rounded-lg p-2 ${accent ? "bg-[var(--ssc-navy)] text-white" : "bg-[var(--ssc-paper)]"}`}>
    <div className={`text-[9px] font-bold uppercase tracking-widest ${accent ? "text-white/70" : "text-[var(--ssc-ink-2)]"}`}>{label}</div>
    <div className={`text-sm font-black ${accent ? "text-white" : "text-[var(--ssc-ink)]"}`}>{value}</div>
  </div>
);

function AddNote({ code, onAdded }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/dealer/deal/${code}/note`, { note });
      onAdded(data.note);
      setNote("");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-4 flex gap-2" data-testid="add-note-form">
      <input className="ssc-input flex-1" placeholder="Add a follow-up note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="add-note-input" />
      <button disabled={busy} className="ssc-btn ssc-btn-secondary text-sm" data-testid="add-note-submit">Add</button>
    </form>
  );
}
